import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useLinkReferralAccount } from '~/domain/Referral';
import { routes } from '~/routes';
import { RemoteConfigUtils } from '~/utils/RemoteConfigUtils';

interface IReferralLinkMessages {
  readonly title: string;
  readonly description: string;
  readonly isActive: boolean;
  readonly showIcon: boolean;
  readonly isSuccess?: boolean;
  readonly isError?: boolean;
}

export const useApplyReferralLink = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { shortLink } = state || {};

  const {
    mutate: linkReferralAccount,
    errorMessage,
    isPending,
    isSuccess,
  } = useLinkReferralAccount();
  const [showPopup, setShowPopup] = useState({
    isOpen: false,
    title: '',
    description: '',
    isSuccess: false,
    showIcon: false,
  });

  const hasSubmittedReferralLinkRef = useRef(false);

  const referralLinkMessages = useMemo(
    () => RemoteConfigUtils.getReferralLinkMessages() as IReferralLinkMessages[],
    [],
  );

  const submitReferralLink = useCallback(
    (referralLink: string) => {
      linkReferralAccount(referralLink, {
        onSuccess: (data) => {
          const referralLinkMessage = referralLinkMessages?.find((message) => message.isSuccess);

          const title = referralLinkMessage
            ? referralLinkMessage.title
            : 'Referral Success. Rewards Await!';
          const description = referralLinkMessage
            ? referralLinkMessage.description.replace('%NAME%', data.senderFirstName)
            : // eslint-disable-next-line max-len
              `The referral link from ${data.senderFirstName} has successfully been applied to your account! Complete your first shift with one of our professionals and you’ll earn a $50 e-gift card as our way of saying thanks.`;

          setShowPopup({
            isOpen: true,
            title,
            description,
            isSuccess: true,
            showIcon: true,
          });
          navigate(routes.referral);
        },
      });
    },
    [linkReferralAccount, referralLinkMessages, navigate],
  );

  useEffect(() => {
    if (shortLink && !hasSubmittedReferralLinkRef.current) {
      submitReferralLink(shortLink);
      navigate(routes.referral);
      hasSubmittedReferralLinkRef.current = true;
    }
  }, [shortLink, submitReferralLink, navigate]);

  useEffect(() => {
    if (errorMessage) {
      const referralLinkMessage = referralLinkMessages?.find(
        (message) => message.title === errorMessage,
      );

      if (referralLinkMessage) {
        setShowPopup({
          isOpen: true,
          title: referralLinkMessage.title,
          description: referralLinkMessage.description,
          isSuccess: false,
          showIcon: !!referralLinkMessage.showIcon,
        });
      }
    }
  }, [errorMessage, referralLinkMessages]);

  const handleClosePopup = () => {
    setShowPopup({
      isOpen: false,
      title: '',
      description: '',
      isSuccess: false,
      showIcon: false,
    });
  };

  return {
    shortLink,
    isLoading: isPending,
    showPopup,
    handleClosePopup,
    isSuccess,
    errorMessage,
    submitReferralLink,
  };
};
